import React, {
    useContext, useCallback, useMemo, useState, useEffect
} from 'react';
import _ from 'lodash';
import { TranslatorContext } from '@jutro/locale';
import { DocumentService } from 'gw-capability-policydocument';
import { SgDocumentsUtil, SgSessionStorageService } from 'saga-modules';
import {
    Button,
    Link,
    Loader,
    DropdownSelectField
} from '@jutro/components';
import { ViewModelServiceContext } from 'gw-portals-viewmodel-react';
import { SgCvcService } from 'sg-capability-cvc';
import CommonConstants from 'sg-common-constants';
import CepConstants from 'sg-cep-constants';
import { useHistory } from 'react-router-dom';
import messages from './SgDocumentDetails.messages';

function SgDocumentDetails() {
    const translator = useContext(TranslatorContext);
    const history = useHistory();

    const [policies, setPolicies] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [policyEditMode, setPolicyEditMode] = useState(undefined);
    const [documentIdsToSent, setDocumentIdsToSent] = useState([]);
    const [documentsSentForPolicies, setDocumentsSentForPolicies] = useState([]);
    const [isDisablePostButton, setIsDisablePostButton] = useState(false);
    const [ddaFormat, setDdaFormat] = useState(SgSessionStorageService.getItem(SgSessionStorageService.KEY.ACCOUNT_DDA_FORMAT));

    const viewModelService = useContext(ViewModelServiceContext);

    useEffect(() => {
        const retrievedDdaFormat = SgSessionStorageService.getItem(SgSessionStorageService.KEY.ACCOUNT_DDA_FORMAT);
        const accPolicyNumber = _.get(policies[0], 'policyNumber');
        if (!_.isEmpty(accPolicyNumber) && _.isEmpty(retrievedDdaFormat)) {
            SgCvcService.getPolicyholderVulnerability(accPolicyNumber).then((result) => {
                if (typeof result === 'string') {
                    SgSessionStorageService.setItem(SgSessionStorageService.KEY.ACCOUNT_DDA_FORMAT, result);
                    setDdaFormat(result);
                }
            }).catch(_.noop);
        }
    }, [policies]);

    const ddaAvailableValues = () => {
        const typelist = viewModelService.productMetadata.get('pc').types.getTypelist('DDA_Ext').getFilter('DocumentTypes').codes;
        return typelist.map((value) => {
            return {
                ...value,
                name: translator({
                    id: `typekey.DDA_Ext.${value.code}`,
                    defaultMessage: ''
                })
            };
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    };

    const displayedDocuments = useCallback((policy) => {
        return policyEditMode === policy.policyNumber
            ? policy.policyDocuments.filter((doc) => doc.canBePosted)
            : policy.policyDocuments;
    }, [policyEditMode]);

    const documentsToSendHandler = useCallback((documentId) => {
        if (documentIdsToSent.includes(documentId)) {
            documentIdsToSent.splice(documentIdsToSent.indexOf(documentId), CepConstants.DELETE_COUNT);
            setDocumentIdsToSent([...documentIdsToSent]);
        } else {
            setDocumentIdsToSent([...documentIdsToSent, documentId]);
        }
    }, [documentIdsToSent]);

    const convertType = (type) => type.replace(CepConstants.PREFIX_TYPE, '');

    const isDocumentsWereSent = useCallback((policyNumber) => {
        return documentsSentForPolicies
            .find((documentSent) => documentSent.policyNumber === policyNumber);
    }, [documentsSentForPolicies]);

    const showPostButton = useMemo(() => {
        return documentIdsToSent.length > 0;
    }, [documentIdsToSent.length]);

    const showPostLink = useCallback((policy) => {
        return policyEditMode !== policy.policyNumber && !isDocumentsWereSent(policy.policyNumber)
            && policy.policyDocuments.some((doc) => doc.canBePosted);
    }, [isDocumentsWereSent, policyEditMode]);

    const getNumberDocuments = useCallback((policyNumber) => {
        return documentsSentForPolicies
            .find((documentSent) => documentSent.policyNumber === policyNumber).numberDocuments;
    }, [documentsSentForPolicies]);

    const goToFaqPage = useCallback((event) => {
        if (event) {
            event.preventDefault();
        }
        history.push('/faq');
    }, [history]);

    useEffect(() => {
        DocumentService.getPoliciesDocuments().then((result) => {
            setPolicies(result);
        }).finally(() => {
            setIsLoading(false);
        });
    }, []);

    const sendDocuments = useCallback((policyNumber) => {
        setIsLoading(true);
        setIsDisablePostButton(true);
        const documentsSentForPolicy = {
            policyNumber: policyNumber,
            numberDocuments: documentIdsToSent.length
        };

        SgSessionStorageService.setItem(SgSessionStorageService.KEY.ACCOUNT_DDA_FORMAT, ddaFormat);
        if (ddaFormat !== CommonConstants.DDA_FORMAT_CODE.NONE) {
            SgCvcService.updatePolicyholderVulnerability(policyEditMode, ddaFormat);
        }

        DocumentService.postDocumentsWithAlternativeFormat(documentIdsToSent, ddaFormat).then(() => {
            setPolicyEditMode(undefined);
            setDocumentsSentForPolicies([...documentsSentForPolicies, documentsSentForPolicy]);
            setDocumentIdsToSent([]);
            setIsDisablePostButton(false);
            setIsLoading(false);
        });
    }, [documentIdsToSent, documentsSentForPolicies, ddaFormat, policyEditMode]);

    const policyTableContent = useCallback((policy) => {
        return (
            <>
                <table className="sg-policy-documents-table">
                    <tbody>
                        {displayedDocuments(policy).map((document, index) => {
                            return (
                                <tr key={document.docUID}>
                                    {policyEditMode === policy.policyNumber && (
                                        <td>
                                            <label
                                                className="sg-checkbox-and-label-container"
                                                htmlFor={`checkboxForDocument${index}`}
                                            >
                                                <span>{document.type}</span>
                                                <input
                                                    id={`checkboxForDocument${index}`}
                                                    type="checkbox"
                                                    onClick={() => documentsToSendHandler(document.publicID)}
                                                    tabIndex="0"
                                                />
                                                <span className="sg-fake-checkbox" />
                                            </label>
                                        </td>
                                    )}
                                    <td>
                                        <label
                                            className="sg-checkbox-label"
                                            htmlFor={`checkboxForDocument${index}`}
                                        >
                                            {document.type === CepConstants.NEW_BUSINESS_ONE_STOP_DOCUMENT_TYPE
                                                ? translator(messages.NewBusinessInformation) : convertType(document.type)}
                                        </label>
                                    </td>
                                    {(policyEditMode !== policy.policyNumber) && (
                                        <td>
                                            <Link
                                                href="/"
                                                id={`downloadDocument${index}`}
                                                onClick={(event) => SgDocumentsUtil.downloadDocument(event, document.policyNumber, document.sagaType)}
                                            >
                                                {translator(messages.Download)}
                                            </Link>
                                        </td>
                                    )}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                {policyEditMode === policy.policyNumber && (
                    <DropdownSelectField
                        id="ddaFormat"
                        label={messages.DdaFormatLabel}
                        availableValues={ddaAvailableValues()}
                        onValueChange={(newValue) => {
                            setDdaFormat(newValue);
                        }}
                        showValidationIcon={false}
                        value={ddaFormat}
                        defaultValue={CommonConstants.DDA_FORMAT_CODE.NONE}
                    />
                )}
            </>
        );
    }, [ddaFormat, displayedDocuments, documentsToSendHandler, policyEditMode, translator]);

    const footerPolicyNoEditModeContent = useCallback((policy, index) => {
        const editModeHandler = (event) => {
            if (event) {
                event.preventDefault();
            }
            setDocumentIdsToSent([]);
            setPolicyEditMode(policy.policyNumber);
        };
        return (
            <div className="sg-policy-documents-tile-footer-container">
                {showPostLink(policy) && (
                    <div id={`footerPolicyNoEditModeFor${index}`} className="sg-policy-documents-footer">
                        <p>
                            {translator(messages.Click)}
                            &nbsp;
                            <Link
                                id={`postDocumentsFor${index}`}
                                onClick={editModeHandler}
                                href="/"
                            >
                                {translator(messages.Here)}
                                &nbsp;
                            </Link>
                            {translator(messages.DocumentsByPost)}
                            <br />
                            {translator(messages.ConsiderEnvironment)}
                        </p>
                    </div>
                )}
            </div>
        );
    }, [showPostLink, translator]);

    const footerPolicyEditModeContent = useCallback((policy, index) => {
        return (
            <div className="sg-policy-documents-tile-footer-container">
                {(policyEditMode === policy.policyNumber && !isDocumentsWereSent(policy.policyNumber)) && (
                    <div id={`footerPolicyEditModeFor${index}`} className="sg-policy-documents-footer">
                        <p>{translator(messages.TickDocuments)}</p>
                        {showPostButton && (
                            <Button
                                type="button"
                                id={`sendDocumentsFor${policy.policyNumber}`}
                                disabled={isDisablePostButton}
                                onClick={() => (sendDocuments(policy.policyNumber))}
                                className="sgButton sgPrimaryButton"
                            >
                                {translator(messages.PostNow)}
                            </Button>
                        )}
                    </div>
                )}
            </div>
        );
    }, [isDisablePostButton, isDocumentsWereSent, policyEditMode, sendDocuments, showPostButton, translator]);

    const documentSentForContent = useCallback((policy, index) => {
        return (
            <div className="sg-policy-documents-tile-footer-container sg-policy-documents-sent">
                {isDocumentsWereSent(policy.policyNumber) && (
                    <div id={`documentsWereSentFor${index}`} className="sg-policy-documents-footer">
                        <p>
                            <span>{translator(messages.DocumentsSent, { numberDocuments: getNumberDocuments(policy.policyNumber) })}</span>
                            <span>{translator(messages.CvcNotification)}</span>
                            <Link
                                id={`readMoreDocumentsLink${index}`}
                                href="/"
                                onClick={goToFaqPage}
                            >
                                {translator(messages.ReadMore)}
                            </Link>
                        </p>
                    </div>
                )}
            </div>
        );
    }, [getNumberDocuments, isDocumentsWereSent, translator]);

    const policyBookContent = useMemo(() => {
        return (
            <>
                <h3>{translator(messages.ClickHere)}</h3>
                <div className="sgPolicyBookListContainer">
                    <ul className="sgMarkerList">
                        <li>
                            <div className="sgMarkerListItem" />
                            <Link
                                id="motorPolicyBook"
                                href={CepConstants.CAR_POLICY_BOOK_LINK}
                                rel="noopener noreferrer"
                                target="_blank"
                            >
                                {translator(messages.MotorLink)}
                            </Link>
                        </li>
                        <li>
                            <div className="sgMarkerListItem" />
                            <Link
                                id="homePolicyBook"
                                href={CepConstants.HOME_POLICY_BOOK_LINK}
                                rel="noopener noreferrer"
                                target="_blank"
                            >
                                {translator(messages.HomeLink)}
                            </Link>
                        </li>
                        <li>
                            <div className="sgMarkerListItem" />
                            <Link
                                id="landlordPolicyBook"
                                href={CepConstants.LANDLORD_POLICY_BOOK_LINK}
                                rel="noopener noreferrer"
                                target="_blank"
                            >
                                {translator(messages.LandlordLink)}
                            </Link>
                        </li>
                    </ul>
                    <ul className="sgMarkerList">
                        <li>
                            <div className="sgMarkerListItem" />
                            <Link
                                id="healthPolicyBook"
                                href={CepConstants.HEALTH_POLICY_BOOK_LINK}
                                rel="noopener noreferrer"
                                target="_blank"
                            >
                                {translator(messages.HealthLink)}
                            </Link>
                        </li>
                        <li>
                            <div className="sgMarkerListItem" />
                            <Link
                                id="travelPolicyBook"
                                href={CepConstants.TRAVEL_POLICY_BOOK_LINK}
                                rel="noopener noreferrer"
                                target="_blank"
                            >
                                {translator(messages.TravelLink)}
                            </Link>
                        </li>
                    </ul>
                </div>
            </>
        );
    }, [translator]);

    const policyDataContent = useMemo(() => {
        return (
            <>
                {policies && policies.map((policy, index) => {
                    return (
                        <div key={policy.policyNumber} id={`policyDetails_${index}`} className="sg-policy-tile sg-mta-policy-tile">
                            <div className={`sg-mta-policy-tile-details-container ${policy.productCode}`}>
                                <div id={`policy${index}`}>
                                    <h3 className="sg-policy-description">
                                        {policy.policyHeader.header}
                                        <span className="sg-policy-registration-number">
                                            { policy.policyHeader.subHeader && (
                                                <>
                                                    <span className="separator" />
                                                    {policy.policyHeader.subHeader}
                                                </>
                                            )}
                                            { policy.policyHeader.currentOrRenewed && (
                                                <span className="separator" />)}
                                            {
                                                policy.policyHeader.currentOrRenewed
                                            }
                                        </span>
                                    </h3>
                                    {policyTableContent(policy)}
                                </div>
                            </div>
                            <>{footerPolicyNoEditModeContent(policy, index)}</>
                            <>{footerPolicyEditModeContent(policy, index)}</>
                            <>{documentSentForContent(policy, index)}</>
                        </div>
                    );
                })}
            </>
        );
    }, [documentSentForContent, footerPolicyEditModeContent, footerPolicyNoEditModeContent, policies, policyTableContent]);

    if (isLoading) {
        return <Loader loaded={!isLoading} />;
    }

    return (
        <div>
            {policyDataContent}
            <div id="policyBooks">{policyBookContent}</div>
        </div>
    );
}
export default SgDocumentDetails;
